'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'light' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/** 🔹 Extracted Variant Styles */
const variantStyles = {
  primary:
    'bg-gradient-to-r from-primary-500 to-accent-blue text-white hover:from-primary-600 hover:to-accent-blue focus:ring-primary-500 shadow-md hover:shadow-xl',
  secondary:
    'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 shadow-md hover:shadow-lg',
  light:
    'bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500 border border-gray-200 shadow-sm hover:shadow',
  danger:
    'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 focus:ring-red-500 shadow-md hover:shadow-lg',
  success:
    'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 focus:ring-green-500 shadow-md hover:shadow-lg',
};

/** 🔹 Extracted Size Styles */
const sizeStyles = {
  sm: 'px-2 py-2 my-1.5 text-xs sm:px-3 sm:py-1.5 sm:text-sm',
  md: 'px-2 py-2.5 my-1.5 text-sm sm:px-4 sm:py-2 sm:text-base',
  lg: 'px-4 py-3 mx-2 my-2 text-base sm:px-5 sm:py-2.5 sm:text-lg',
};

export function Button({
  children,
  className,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex transform items-center justify-center rounded-lg font-semibold tracking-wide transition-all duration-300 ease-out hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95',
        variantStyles[variant],
        sizeStyles[size],
        (isLoading || disabled) && 'cursor-not-allowed opacity-70',
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {leftIcon && !isLoading && (
        <span className="mr-1 sm:mr-2">{leftIcon}</span>
      )}

      {isLoading ? (
        <div className="flex items-center space-x-1 sm:space-x-1.5">
          <Spinner size="sm" className="text-current" />
          <span className="text-xs sm:text-sm">Loading...</span>
        </div>
      ) : (
        children
      )}

      {rightIcon && !isLoading && (
        <span className="ml-1 sm:ml-2">{rightIcon}</span>
      )}
    </button>
  );
}
